import { useThemeStore } from "@stores/theme.store";
import { Button, Select } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";
import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { FinanceService } from "@services/finance.service";
import { VirtualAccountReport } from "@services/dtos/finance.dto";
import { FcFinePrint } from "react-icons/fc";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import moment from "moment-timezone";
import { FilterOutlined } from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
moment.locale("id");

export const VaDataPage = () => {
  const navigate = useNavigate();
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (state) => state,
  );
  const [limit, setLimit] = useState<string>("10");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data, isPending } = useQuery({
    queryKey: [FinanceService.queries.GET_FINANCE_BILLS_PAID_OF, nis, limit],
    queryFn: () => FinanceService.virtualAccountReport({ nis, limit }),
  });

  const onOpenDetail = (dataPayment: VirtualAccountReport) => {
    navigate(linkPage.VA_DETAIL_PAGE, { state: dataPayment });
  };

  useEffect(() => {
    setAppBar({ title: "Laporan Pembayaran VA", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Laporan Pembayaran VA",
      image: false,
    });
    setBottomNavigation("home");
  }, [setHeader, setAppBar, setBottomNavigation]);

  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {/* set filter */}
        <div className="tw-pt-0 tw-text-background-100">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-w-2/3 pocket-highlight">
              <div className="tw-text-base tw-text-background-100">
                Data yang ditampilkan{" "}
                <span className="tw-font-bold">{limit} data terakhir</span>
              </div>
            </div>
            <div className="pocket-filter">
              <FilterOutlined
                className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
                onClick={() => setPopupFilter(true)}
              />
            </div>
          </div>
        </div>
        {/* end of set filter */}

        {/* report */}
        <div className="tw-mt-8">
          {isPending ? (
            <div className="tw-text-center card-body">
              <Spiner />
            </div>
          ) : data?.data != null && data.data.length > 0 ? (
            <>
              {data.data.map((item, idx) => (
                <div
                  key={idx}
                  className="tw-mb-4 tw-py-2 card-body"
                  onClick={() => onOpenDetail(item)}
                >
                  <div className="tw-flex tw-justify-between tw-items-center tw-font-semibold">
                    <div className="tw-text-xs tw-text-text-secondary tw-font-medium ">
                      {item.vaStatus === "COMPLETED" ? "Bayar: " : "Exp: "}
                      {item.vaStatus === "COMPLETED"
                        ? moment(item.vaDatePayment ?? Date.now()).format(
                            "DD MMM YYYY | HH:mm",
                          )
                        : moment(item.vaDateExpired ?? Date.now()).format(
                            "DD MMM YYYY | HH:mm",
                          )}
                    </div>
                    <div className="tw-w-1/3 tw-flex tw-justify-end">
                      {item.vaStatus === "PENDING" ? (
                        <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-yellow-50 tw-px-2 tw-py-1 tw-text-[10px] tw-font-medium tw-text-yellow-700 tw-ring-1 tw-ring-yellow-600/10 tw-ring-inset">
                          PENDING
                        </span>
                      ) : item.vaStatus === "EXPIRED" ? (
                        <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-red-50 tw-px-2 tw-py-1 tw-text-[10px] tw-font-medium tw-text-red-700 tw-ring-1 tw-ring-red-600/10 tw-ring-inset">
                          EXPIRED
                        </span>
                      ) : (
                        <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-green-50 tw-px-2 tw-py-1 tw-text-[10px] tw-font-medium tw-text-green-700 tw-ring-1 tw-ring-green-600/10 tw-ring-inset">
                          COMPLETE
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="tw-pb-2 tw-mb-2 tw-flex tw-justify-between tw-items-end tw-font-semibold tw-border-primary"
                    style={{ borderBottom: "1px solid" }}
                  >
                    <div className="tw-text-sm tw-text-text-primary">
                      {item.vaTrxId}
                    </div>
                    <div className="tw-w-1/2 tw-text-end">
                      <div className="tw-text-base tw-text-text-primary">
                        {toRp(
                          item.vaBankName.toLowerCase() === "bni"
                            ? (item.vaTrxAmount ?? 0) + (item.vaBankFee ?? 0)
                            : (item.vaTrxAmount ?? 0),
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="tw-text-xs tw-text-end tw-text-text-tertiary tw-italic">
                    klik untuk melihat detail
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="tw-p-8 tw-text-center card-body">
              <FcFinePrint size={50} />
              <div>Data tidak ditemukan</div>
            </div>
          )}
        </div>
        {/* report */}
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <div className="">
          <Select
            className=" tw-text-base"
            placeholder="Pilih Jumlah Ditampilkan"
            defaultValue={limit}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setLimit(e)}
            options={[
              { value: "5", label: "5 data terakhir" },
              { value: "10", label: "10 data terakhir" },
              { value: "25", label: "25 data terakhir" },
            ]}
          ></Select>
          <Button
            onClick={() => setPopupFilter(false)}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl tw-bg-red-500 tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>
    </>
  );
};
